<template>
  <filter-and-sort-box
    class="expandabeltable"
    :text="text"
    :data="data"
    :headers="headers"
    :columnsToSort="columnsToSort"
    :useSearch="useSearch"
    @searchQuery="searchQuery"
    :columnsToNotSort="columnsToNotSort"
    :defaultSortKey="defaultSortKey"
    :defaultSortOrder="defaultSortOrder"
  >
    <template v-slot:rows="{ rows }">
      <tr
        class="relative"
        v-for="(d, index) in getRows(rows)"
        :class="{
          child: d.isChild,
          'last-child': d.isLastChild,
          parent: !d.isChild,
          paddingRow: Object.keys(d).length === 0,
          expanded: isRowExpanded(d)
        }"
        :key="'data' + index"
        @click="expand(d)"
      >
        <td
          class="relative"
          :colspan="d.isChild ? Object.keys(headers).length + 1 : 1"
          v-for="(key, i) in Object.keys(headers)"
          :key="key + i"
          v-show="!d.isChild || (d.isChild && i === 0)"
        >
          <div v-if="!d.isChild">
            <slot name="column" :column="key" :child="d">{{ d[key] }}</slot>
          </div>
          <div v-else>
            <slot name="child" :child="d"></slot>
          </div>
        </td>
        <td class="relative w-1" v-if="!d.isChild">
          <i
            class="p-1 arrow"
            :class="isRowExpanded(d) ? 'down border-primary' : 'right'"
          ></i>
        </td>
      </tr>
    </template>
    <div v-if="renderComponent"></div>
  </filter-and-sort-box>
</template>

<script>
import FilterAndSortBox from "components/FilterAndSortBox.vue";

export default {
  props: {
    data: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Object,
      default: () => {}
    },
    columnsToSort: {
      type: Array,
      default: () => []
    },
    columnsToNotSort: {
      type: Array,
      default: () => []
    },
    text: {
      type: String,
      default: ""
    },
    useSearch: {
      type: Boolean,
      default: true
    },
    defaultSortKey: {
      type: String,
      default: null
    },
    defaultSortOrder: {
      type: Number,
      default: 1
    }
  },
  components: {
    FilterAndSortBox
  },
  data() {
    return {
      expanded: {},
      renderComponent: false
    };
  },
  methods: {
    searchQuery(value) {
      this.$emit("searchQuery", value);
    },
    isRowExpanded(row) {
      if (row.isChild || Object.keys(row).length === 0) return;
      const id = "data" + row.id;
      return this.expanded[id];
    },
    expand(row) {
      if (row.isChild || Object.keys(row).length === 0) return;
      const id = "data" + row.id;
      this.expanded[id] = !this.expanded[id];
      this.forceRerender();
    },
    getRows(rows) {
      const scope = this;
      const r = [];
      rows
        .filter(row => !row.isChild)
        .forEach(row => {
          const id = "data" + row.id;
          r.push(row);
          if (scope.expanded[id] === undefined) scope.expanded[id] = false;
          if (scope.expanded[id] && row.expandableData) {
            row.expandableData.isChild = true;
            row.expandableData.parentId = row.id;
            r.push(row.expandableData);
            row.expandableData.isLastChild = true;
          }
          r.push({});
        });
      return r;
    },
    forceRerender() {
      // Remove my-component from the DOM
      this.renderComponent = true;

      this.$nextTick(() => {
        // Add the component back in
        this.renderComponent = false;
      });
    }
  }
};
</script>

<style lang="scss">
.expandabeltable {
  margin-bottom: 20px;
  border-radius: 6px;
  color: theme("colors.textcolor");

  .table {
    .filter-table {
      .filter-body {
        background-color: transparent !important;

        > tr {
          background-color: theme("colors.primary");

          &.paddingRow {
            color: transparent;
            background-color: transparent;
            border: none;

            * {
              display: none;
            }

            > td {
              display: table-cell;
              color: transparent;
              background-color: transparent;
              border: none;
              padding: 0.25rem;
            }
          }

          $borde-size: 0.2rem;
          &.parent:not(.paddingRow) {
            &.expanded {
              > td {
                border-top: 1px solid theme("colors.textcolor");
                border-bottom: 1px solid theme("colors.textcolor");
                background-color: white;
                > * {
                  color: theme("colors.primary");
                }
              }
              > td:first-child {
                border-left: 1px solid theme("colors.textcolor");
                border-radius: $borde-size 0 0 0;
                > * {
                  color: theme("colors.highlight");
                }
              }
              > td:last-child {
                border-right: 1px solid theme("colors.textcolor");
                border-radius: 0 $borde-size 0 0;
              }
            }
            &:not(.expanded) {
              > td:first-child {
                border-radius: $borde-size 0 0 $borde-size;
              }
              > td {
                border: none;
              }
              > td:last-child {
                border-radius: 0 $borde-size $borde-size 0;
              }
            }
          }

          &.child {
            > td:first-child {
              border-left: 1px solid theme("colors.textcolor");
              border-right: 1px solid theme("colors.textcolor");
            }

            > td {
              background-color: theme("colors.primary");
            }
          }
          &.last-child {
            > td:first-child {
              border-radius: 0 0 $borde-size $borde-size;
              border-bottom: 1px solid theme("colors.textcolor");
            }
          }
        }
      }

      > thead > tr > th,
      > tbody > tr > td {
        padding: 1rem;
      }
    }
  }
}
</style>
